import type { LocalizedCopy } from "../productCopyTypes";

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>\n${p}\n</p>`)
    .join("\n\n");
}

function featureList(items: string[]): string {
  return `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
}

const COPY = {
  en: {
    title: "Garage",
    description:
      "Garage is a highly detailed brick-built workshop designed for collectors, car enthusiasts, and fans of realistic display sets. With 1,047 pieces, this compact but richly detailed model recreates a professional-style automotive garage packed with tools, equipment, accessories, and workshop details.\n\nThe open-wall layout makes the entire interior easy to access and admire. The garage includes a detailed work area with tool storage, shelves, wheels, tires, an air compressor, work equipment, a counter, and numerous small accessories that bring the scene to life.\n\nA major highlight is the modular design. Many of the interior elements can be positioned or removed, allowing the garage layout to be customized to your preference. The set can be displayed on its own or combined with other vehicle and workshop-themed sets to create a larger automotive scene.\n\nWith its realistic proportions, detailed interior, and strong display presence, Garage is an excellent project for builders who enjoy creating miniature environments rather than simply assembling a vehicle.",
    highlightsTitle: "Product Features",
    features: [
      "Brand: 12GO",
      "Set Number: 95023",
      "Model: Garage",
      "Pieces: 1,047",
      "Recommended Age: 14+",
      "Detailed automotive workshop",
      "Open-wall display design",
      "Extensive interior detailing",
      "Tool storage and workshop equipment",
      "Tires and wheels",
      "Air compressor",
      "Shelving and work areas",
      "Multiple accessories and small details",
      "Modular interior elements",
      "Designed for building, display, and collection",
    ],
    specsTitle: "Specifications",
    specs: [
      "<li><strong>Brand:</strong> 12GO</li>",
      "<li><strong>Set Number:</strong> 95023</li>",
      "<li><strong>Model:</strong> Garage</li>",
      "<li><strong>Pieces:</strong> 1,047</li>",
      "<li><strong>Recommended Age:</strong> 14+</li>",
    ],
  },
  pl: {
    title: "Garage",
    description:
      "Garage to szczegółowy model warsztatu samochodowego wykonany z klocków konstrukcyjnych, stworzony z myślą o kolekcjonerach, miłośnikach motoryzacji oraz osobach ceniących realistyczne modele ekspozycyjne. Zestaw składa się z 1047 elementów i przedstawia profesjonalnie wyposażony garaż pełen narzędzi, akcesoriów i detali warsztatowych.\n\nOtwarta konstrukcja pozwala łatwo zobaczyć całe wnętrze i wszystkie jego elementy. W garażu znajdziemy między innymi miejsce na narzędzia, półki, koła i opony, kompresor, wyposażenie warsztatowe, stanowisko pracy oraz liczne drobne akcesoria, które nadają całej scenie wyjątkowego realizmu.\n\nDużą zaletą jest modułowa konstrukcja. Poszczególne elementy wyposażenia można przestawiać lub zdejmować, dzięki czemu układ garażu można dopasować do własnych upodobań. Model może być eksponowany samodzielnie lub stać się częścią większej kolekcji związanej z motoryzacją i warsztatami.\n\nGarage to ciekawa propozycja dla osób, które lubią budować nie tylko pojedyncze pojazdy, ale również kompletne miniaturowe środowiska pełne szczegółów.",
    highlightsTitle: "Cechy produktu",
    features: [
      "Marka: 12GO",
      "Numer zestawu: 95023",
      "Model: Garage",
      "Liczba elementów: 1047",
      "Wiek: 14+",
      "Szczegółowo odwzorowany warsztat samochodowy",
      "Otwarta konstrukcja ułatwiająca ekspozycję",
      "Bogato wyposażone wnętrze",
      "Szafki i miejsce na narzędzia",
      "Koła i opony",
      "Kompresor",
      "Półki i stanowiska warsztatowe",
      "Liczne akcesoria i drobne detale",
      "Modułowe elementy wyposażenia",
      "Idealny do budowy, kolekcjonowania i ekspozycji",
    ],
    specsTitle: "Specyfikacja",
    specs: [
      "<li><strong>Marka:</strong> 12GO</li>",
      "<li><strong>Numer zestawu:</strong> 95023</li>",
      "<li><strong>Model:</strong> Garage</li>",
      "<li><strong>Liczba elementów:</strong> 1047</li>",
      "<li><strong>Wiek:</strong> 14+</li>",
    ],
  },
  de: {
    title: "Garage",
    description:
      "Garage ist ein detailreiches Klemmbausteinmodell einer Autowerkstatt, das speziell für Sammler, Autofans und Liebhaber realistischer Displaymodelle entwickelt wurde. Mit 1.047 Teilen entsteht eine kompakte, aber äußerst abwechslungsreiche Werkstattszene mit Werkzeugen, Ausrüstung und zahlreichen kleinen Details.\n\nDank der offenen Bauweise lässt sich das komplette Interieur hervorragend betrachten. Die Garage verfügt unter anderem über Werkzeugaufbewahrung, Regale, Räder und Reifen, einen Kompressor, Werkstattausrüstung, Arbeitsbereiche und zahlreiche Zubehörteile, die der Szene einen authentischen Charakter verleihen.\n\nDie modulare Konstruktion ermöglicht es, verschiedene Einrichtungselemente individuell zu positionieren oder zu entfernen. Dadurch kann die Garage nach eigenen Vorstellungen gestaltet werden. Das Modell eignet sich sowohl als eigenständiges Display als auch als Ergänzung zu einer größeren Automobil- oder Werkstattsammlung.\n\nGarage ist ideal für alle, die nicht nur Fahrzeuge bauen, sondern komplette Miniaturszenen mit vielen realistischen Details gestalten möchten.",
    highlightsTitle: "Produkteigenschaften",
    features: [
      "Marke: 12GO",
      "Setnummer: 95023",
      "Modell: Garage",
      "Teile: 1.047",
      "Altersempfehlung: 14+",
      "Detaillierte Autowerkstatt",
      "Offene Bauweise",
      "Umfangreich gestalteter Innenraum",
      "Werkzeugaufbewahrung",
      "Räder und Reifen",
      "Kompressor",
      "Regale und Arbeitsbereiche",
      "Zahlreiche Zubehörteile",
      "Modulare Einrichtungselemente",
      "Ideal zum Bauen, Sammeln und Ausstellen",
    ],
    specsTitle: "Spezifikationen",
    specs: [
      "<li><strong>Marke:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 95023</li>",
      "<li><strong>Modell:</strong> Garage</li>",
      "<li><strong>Teile:</strong> 1.047</li>",
      "<li><strong>Altersempfehlung:</strong> 14+</li>",
    ],
  },
  fr: {
    title: "Garage",
    description:
      "Garage est un modèle en briques très détaillé représentant un atelier automobile, conçu pour les collectionneurs, les passionnés de voitures et les amateurs de modèles réalistes. Avec 1 047 pièces, il recrée un atelier compact mais richement équipé, rempli d'outils, d'accessoires et de nombreux détails.\n\nSa conception ouverte permet d'admirer facilement l'ensemble de l'intérieur. Le garage comprend notamment des rangements pour outils, des étagères, des roues et des pneus, un compresseur, du matériel d'atelier, des espaces de travail et de nombreux petits accessoires.\n\nLa conception modulaire permet de repositionner ou de retirer certains éléments de l'aménagement afin de personnaliser la scène. Le modèle peut être exposé seul ou intégré à une collection plus vaste consacrée à l'automobile et aux ateliers.\n\nGarage est un excellent choix pour les constructeurs qui aiment créer des environnements miniatures détaillés, et pas seulement des véhicules.",
    highlightsTitle: "Caractéristiques",
    features: [
      "Marque : 12GO",
      "Référence : 95023",
      "Modèle : Garage",
      "Nombre de pièces : 1 047",
      "Âge recommandé : 14+",
      "Atelier automobile détaillé",
      "Construction ouverte pour une présentation optimale",
      "Intérieur richement détaillé",
      "Rangements pour outils",
      "Roues et pneus",
      "Compresseur",
      "Étagères et espaces de travail",
      "Nombreux accessoires",
      "Éléments d'aménagement modulaires",
      "Idéal pour construction, collection et exposition",
    ],
    specsTitle: "Spécifications",
    specs: [
      "<li><strong>Marque :</strong> 12GO</li>",
      "<li><strong>Référence :</strong> 95023</li>",
      "<li><strong>Modèle :</strong> Garage</li>",
      "<li><strong>Nombre de pièces :</strong> 1 047</li>",
      "<li><strong>Âge recommandé :</strong> 14+</li>",
    ],
  },
  es: {
    title: "Garage",
    description:
      "Garage es un modelo de bloques de construcción que recrea un taller automovilístico lleno de detalles, pensado para coleccionistas, aficionados a los coches y amantes de los modelos de exposición realistas. Con 1.047 piezas, ofrece una escena compacta pero muy completa.\n\nSu diseño abierto permite disfrutar fácilmente del interior y de todos sus elementos. El garaje incluye almacenamiento para herramientas, estanterías, ruedas y neumáticos, un compresor, equipamiento de taller, zonas de trabajo y numerosos accesorios.\n\nLa construcción modular permite colocar o retirar diferentes elementos del interior y personalizar la distribución del taller. Puede exponerse como un modelo independiente o integrarse en una colección más amplia de vehículos y escenarios automovilísticos.\n\nGarage es una excelente opción para quienes disfrutan creando entornos en miniatura detallados y llenos de personalidad.",
    highlightsTitle: "Características",
    features: [
      "Marca: 12GO",
      "Número de set: 95023",
      "Modelo: Garage",
      "Piezas: 1.047",
      "Edad recomendada: 14+",
      "Taller automovilístico detallado",
      "Diseño abierto para una mejor exposición",
      "Interior ampliamente detallado",
      "Almacenamiento para herramientas",
      "Ruedas y neumáticos",
      "Compresor",
      "Estanterías y zonas de trabajo",
      "Numerosos accesorios",
      "Elementos interiores modulares",
      "Ideal para construcción, colección y exposición",
    ],
    specsTitle: "Especificaciones",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Número de set:</strong> 95023</li>",
      "<li><strong>Modelo:</strong> Garage</li>",
      "<li><strong>Piezas:</strong> 1.047</li>",
      "<li><strong>Edad recomendada:</strong> 14+</li>",
    ],
  },
  nl: {
    title: "Garage",
    description:
      "Garage is een gedetailleerd bouwstenenmodel van een moderne autowerkplaats, ontworpen voor verzamelaars, autoliefhebbers en fans van realistische displaymodellen. Met 1.047 onderdelen ontstaat een compacte maar rijk uitgewerkte werkplaats vol gereedschap, apparatuur en accessoires.\n\nDankzij het open ontwerp is het volledige interieur goed zichtbaar. De garage beschikt onder andere over opslag voor gereedschap, rekken, wielen en banden, een compressor, werkplaatsapparatuur, werkplekken en tal van kleine accessoires.\n\nDe modulaire constructie maakt het mogelijk om verschillende onderdelen van het interieur te verplaatsen of te verwijderen. Zo kan de indeling naar eigen voorkeur worden aangepast. De garage kan zelfstandig worden tentoongesteld of worden gecombineerd met andere voertuig- en werkplaatsmodellen.\n\nGarage is een uitstekende keuze voor bouwers die complete miniatuuromgevingen willen creëren in plaats van alleen voertuigen te bouwen.",
    highlightsTitle: "Productkenmerken",
    features: [
      "Merk: 12GO",
      "Setnummer: 95023",
      "Model: Garage",
      "Onderdelen: 1.047",
      "Aanbevolen leeftijd: 14+",
      "Gedetailleerde autowerkplaats",
      "Open ontwerp voor optimale presentatie",
      "Rijk gedetailleerd interieur",
      "Gereedschapsopslag",
      "Wielen en banden",
      "Compressor",
      "Rekken en werkplekken",
      "Talrijke accessoires",
      "Modulaire interieuronderdelen",
      "Geschikt voor bouwen, verzamelen en tentoonstellen",
    ],
    specsTitle: "Specificaties",
    specs: [
      "<li><strong>Merk:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 95023</li>",
      "<li><strong>Model:</strong> Garage</li>",
      "<li><strong>Onderdelen:</strong> 1.047</li>",
      "<li><strong>Aanbevolen leeftijd:</strong> 14+</li>",
    ],
  },
  it: {
    title: "Garage",
    description:
      "Garage è un dettagliato modello in mattoncini che ricrea un'officina automobilistica, progettato per collezionisti, appassionati di auto e amanti dei modelli da esposizione realistici. Con 1.047 pezzi, il set offre una scena compatta ma ricca di dettagli.\n\nLa struttura aperta permette di vedere facilmente l'intero ambiente interno. Il garage comprende porta-attrezzi, scaffali, ruote e pneumatici, un compressore, attrezzatura da officina, postazioni di lavoro e numerosi piccoli accessori.\n\nGrazie alla costruzione modulare, diversi elementi dell'officina possono essere spostati o rimossi per personalizzare la disposizione. Il modello può essere esposto da solo oppure inserito in una collezione più ampia dedicata al mondo automobilistico.\n\nGarage è perfetto per chi ama costruire ambientazioni in miniatura dettagliate e realistiche, oltre ai singoli veicoli.",
    highlightsTitle: "Caratteristiche",
    features: [
      "Marca: 12GO",
      "Numero set: 95023",
      "Modello: Garage",
      "Pezzi: 1.047",
      "Età consigliata: 14+",
      "Officina automobilistica dettagliata",
      "Struttura aperta per una migliore esposizione",
      "Interni ricchi di dettagli",
      "Porta-attrezzi e sistemi di stoccaggio",
      "Ruote e pneumatici",
      "Compressore",
      "Scaffali e postazioni di lavoro",
      "Numerosi accessori",
      "Elementi interni modulari",
      "Ideale per costruzione, collezione ed esposizione",
    ],
    specsTitle: "Specifiche",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Numero set:</strong> 95023</li>",
      "<li><strong>Modello:</strong> Garage</li>",
      "<li><strong>Pezzi:</strong> 1.047</li>",
      "<li><strong>Età consigliata:</strong> 14+</li>",
    ],
  },
  ru: {
    title: "Garage",
    description:
      "Garage — это детализированная модель автомобильной мастерской из конструктора, созданная для коллекционеров, автолюбителей и поклонников реалистичных моделей для демонстрации. Набор состоит из 1 047 деталей и представляет собой компактную, но насыщенную деталями сцену мастерской.\n\nОткрытая конструкция позволяет хорошо рассмотреть весь интерьер. В гараже предусмотрены места для хранения инструментов, полки, колёса и шины, компрессор, оборудование мастерской, рабочие зоны и множество небольших аксессуаров.\n\nМодульная конструкция позволяет перемещать или снимать отдельные элементы интерьера, изменяя планировку по своему вкусу. Модель можно использовать как самостоятельный экспонат или дополнить ею большую коллекцию автомобильной тематики.\n\nGarage отлично подойдёт тем, кто любит создавать не только отдельные автомобили, но и полноценные миниатюрные окружающие сцены.",
    highlightsTitle: "Характеристики",
    features: [
      "Бренд: 12GO",
      "Номер набора: 95023",
      "Модель: Garage",
      "Количество деталей: 1 047",
      "Рекомендуемый возраст: 14+",
      "Детализированная автомобильная мастерская",
      "Открытая конструкция",
      "Подробно оформленный интерьер",
      "Система хранения инструментов",
      "Колёса и шины",
      "Компрессор",
      "Полки и рабочие зоны",
      "Множество аксессуаров",
      "Модульные элементы интерьера",
      "Подходит для сборки, коллекционирования и экспонирования",
    ],
    specsTitle: "Спецификация",
    specs: [
      "<li><strong>Бренд:</strong> 12GO</li>",
      "<li><strong>Номер набора:</strong> 95023</li>",
      "<li><strong>Модель:</strong> Garage</li>",
      "<li><strong>Количество деталей:</strong> 1 047</li>",
      "<li><strong>Рекомендуемый возраст:</strong> 14+</li>",
    ],
  },
  zh: {
    title: "Garage",
    description:
      "Garage 是一款高细节汽车维修车库积木模型，专为汽车爱好者、收藏家以及喜欢真实场景模型的玩家打造。整套包含 1,047 块积木，通过丰富的工具、设备和配件还原一个充满细节的汽车工作室。\n\n开放式结构让内部空间一览无余。模型中包含工具收纳、置物架、车轮与轮胎、空气压缩机、维修设备、工作区域以及大量细节配件，大幅提升整体场景的真实感。\n\n模块化设计让部分内部设施可以移动或拆卸，可以根据自己的喜好调整车库布局。模型既可以独立展示，也可以与其他汽车及维修主题积木模型组合，打造更完整的汽车场景。\n\nGarage 非常适合喜欢打造完整微型场景，而不仅仅是拼搭车辆的积木爱好者。",
    highlightsTitle: "产品特点",
    features: [
      "品牌： 12GO",
      "产品编号： 95023",
      "型号： Garage",
      "积木数量： 1,047 块",
      "建议年龄： 14 岁以上",
      "高细节汽车维修车库",
      "开放式展示结构",
      "丰富的内部细节",
      "工具收纳区域",
      "车轮与轮胎",
      "空气压缩机",
      "置物架与工作区域",
      "多种场景配件",
      "模块化内部设施",
      "适合拼搭、收藏与展示",
    ],
    specsTitle: "规格",
    specs: [
      "<li><strong>品牌：</strong> 12GO</li>",
      "<li><strong>产品编号：</strong> 95023</li>",
      "<li><strong>型号：</strong> Garage</li>",
      "<li><strong>积木数量：</strong> 1,047 块</li>",
      "<li><strong>建议年龄：</strong> 14 岁以上</li>",
    ],
  },
};

type LocaleKey = keyof typeof COPY;

function buildLocaleHtml(locale: LocaleKey): string {
  const c = COPY[locale] ?? COPY.en;
  return `<h1>${c.title}</h1>

${paragraphs(c.description)}

<h2>${c.specsTitle}</h2>

<ul>
${c.specs.join("\n")}
</ul>

<h2>${c.highlightsTitle}</h2>

${featureList([...c.features])}`;
}

export const RICH_PRODUCT_COPY_95023: LocalizedCopy = {
  en: buildLocaleHtml("en"),
  pl: buildLocaleHtml("pl"),
  de: buildLocaleHtml("de"),
  es: buildLocaleHtml("es"),
  fr: buildLocaleHtml("fr"),
  nl: buildLocaleHtml("nl"),
  zh: buildLocaleHtml("zh"),
  it: buildLocaleHtml("it"),
  ru: buildLocaleHtml("ru"),
};
